/**
 * <SEOHead />
 * ────────────────────────────────────────────────────────────
 * Phase 122 — page-level SEO head manager for every dynamic
 * route on Build Your Book. Adds:
 *   • <title>
 *   • <meta name="description">
 *   • <meta name="robots" content="index,follow,max-image-preview:large">
 *   • <link rel="canonical" href="...">
 *   • <meta property="og:*"> (title, description, url, image, type)
 *   • <meta name="twitter:*"> (card, title, description, image)
 *   • <link rel="alternate" hreflang="..."> per language variant
 *   • Optional JSON-LD block (any object → <script type="application/ld+json">)
 *
 * Fixes 381 "Alternative page with proper canonical tag" errors
 * reported in GSC by giving every page an explicit canonical +
 * OG/twitter/hreflang so Google stops guessing.
 *
 * Query-string sanitisation: canonical URL strips `?ref=`,
 * `?utm_*`, `?state=`, `?fbclid=`, `?gclid=` so query-param
 * variants (from ads, LLM referrals, share links) dedupe to the
 * clean URL.
 *
 * Idempotent - safe to render on every route change; it removes
 * previous BYB-managed tags before writing new ones.
 */
import { useEffect } from 'react';

const SITE = 'https://buildyourbook.in';
const BYB_MANAGED_ATTR = 'data-byb-seo';

// Params we strip when computing the canonical URL. Everything
// else (like `?category=hindi`) is preserved because it changes
// the page content.
const STRIP_PARAMS = new Set([
  'ref', 'fbclid', 'gclid', 'msclkid',
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
  'state',
]);

function cleanCanonical(rawUrl) {
  try {
    const u = new URL(rawUrl);
    const clean = new URLSearchParams();
    for (const [k, v] of u.searchParams.entries()) {
      if (!STRIP_PARAMS.has(k)) clean.append(k, v);
    }
    const qs = clean.toString();
    return `${u.origin}${u.pathname}${qs ? '?' + qs : ''}`;
  } catch {
    return rawUrl;
  }
}

function upsertMeta(selector, attrName, attrValue, content) {
  if (!content) return;
  // Adopt any pre-existing static tag (from public/index.html) so we
  // don't create duplicates. Prefer the one already tagged as BYB-managed;
  // otherwise take over the first matching static tag.
  let el = document.querySelector(`${selector}[${attrName}="${attrValue}"][${BYB_MANAGED_ATTR}]`)
        || document.querySelector(`${selector}[${attrName}="${attrValue}"]`);
  if (!el) {
    el = document.createElement(selector);
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute(BYB_MANAGED_ATTR, '1');
  el.setAttribute('content', content);
}

function upsertLink(rel, href, extraAttrs = {}) {
  if (!href) return;
  const hrefKey = extraAttrs.hreflang ? `[hreflang="${extraAttrs.hreflang}"]` : '';
  let el = document.querySelector(`link[rel="${rel}"]${hrefKey}[${BYB_MANAGED_ATTR}]`)
        || document.querySelector(`link[rel="${rel}"]${hrefKey}`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute(BYB_MANAGED_ATTR, '1');
  el.setAttribute('href', href);
  for (const [k, v] of Object.entries(extraAttrs)) {
    if (v) el.setAttribute(k, v);
  }
}

function removeManagedTags() {
  // Remove BYB-managed JSON-LD script blocks + orphaned duplicate
  // meta/link tags (keeps the ORIGINAL static tag from index.html
  // so we can adopt it on next render).
  document.querySelectorAll(`script[${BYB_MANAGED_ATTR}]`).forEach(n => n.remove());
  // Cleanup hreflang alternates from previous route (they may not
  // apply on the next page).
  document.querySelectorAll(`link[rel="alternate"][${BYB_MANAGED_ATTR}]`).forEach(n => n.remove());
}

function upsertJsonLd(blocks) {
  if (!blocks || !blocks.length) return;
  blocks.forEach((b, i) => {
    if (!b) return;
    const id = `byb-jsonld-${i}`;
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      el.setAttribute(BYB_MANAGED_ATTR, '1');
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(b);
  });
}

/**
 * Props:
 *   title           - full <title> (falls back to Build Your Book default)
 *   description     - meta description
 *   canonical       - canonical URL (optional; defaults to window.location cleaned)
 *   ogImage         - og:image URL
 *   ogType          - og:type ('website'|'article'|'product'|'video.other')
 *   twitterCard     - 'summary'|'summary_large_image'|'player'
 *   robots          - robots directive (defaults to full-index)
 *   hreflangs       - [{ lang: 'hi', href: '...' }, ...]
 *   jsonLd          - array of schema.org JSON-LD blocks
 */
export default function SEOHead({
  title,
  description,
  canonical,
  ogImage,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  robots = 'index,follow,max-image-preview:large,max-snippet:-1',
  hreflangs = [],
  jsonLd = [],
}) {
  useEffect(() => {
    // 1. Reset previously-managed BYB tags (avoids duplicates on
    //    SPA route changes).
    removeManagedTags();

    // 2. Title.
    if (title) document.title = title;

    // 3. Canonical + robots + description.
    const canonUrl = cleanCanonical(canonical || (typeof window !== 'undefined' ? window.location.href : SITE));
    upsertLink('canonical', canonUrl);
    upsertMeta('meta', 'name', 'robots', robots);
    if (description) upsertMeta('meta', 'name', 'description', description);

    // 4. Open Graph.
    upsertMeta('meta', 'property', 'og:type', ogType);
    upsertMeta('meta', 'property', 'og:url', canonUrl);
    if (title) upsertMeta('meta', 'property', 'og:title', title);
    if (description) upsertMeta('meta', 'property', 'og:description', description);
    if (ogImage) upsertMeta('meta', 'property', 'og:image', ogImage);
    upsertMeta('meta', 'property', 'og:site_name', 'Build Your Book');

    // 5. Twitter card.
    upsertMeta('meta', 'name', 'twitter:card', twitterCard);
    if (title) upsertMeta('meta', 'name', 'twitter:title', title);
    if (description) upsertMeta('meta', 'name', 'twitter:description', description);
    if (ogImage) upsertMeta('meta', 'name', 'twitter:image', ogImage);
    upsertMeta('meta', 'name', 'twitter:site', '@BuildYourBookIN');

    // 6. hreflang for language variants.
    hreflangs.forEach(({ lang, href }) => {
      if (lang && href) upsertLink('alternate', href, { hreflang: lang });
    });
    // x-default so Google knows the fallback locale.
    if (hreflangs.length) upsertLink('alternate', canonUrl, { hreflang: 'x-default' });

    // 7. Schema.org JSON-LD blocks.
    if (jsonLd.length) upsertJsonLd(jsonLd);

    // No cleanup on unmount - the NEXT SEOHead render calls
    // removeManagedTags() at the top of its effect. This avoids a
    // 1-frame flash of missing tags on route change.
  }, [title, description, canonical, ogImage, ogType, twitterCard, robots,
      JSON.stringify(hreflangs), JSON.stringify(jsonLd)]);

  return null;
}
